import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import { lookUpUserAction } from '../../actions/lookup';
import FormDivider from '../../components/common/FormDivider';
import LookupForm from '../../components/forms/LookUpForm';
import { IAllContextsProps } from '../../constants/interfaces';
import {
    IConfigContextProps,
    useConfigContext,
} from '../../contexts/ConfigContext';
import {
    IFrontendContext,
    MODAL_TYPES,
    useFrontendContext,
} from '../../contexts/FrontendContext';
import { IUserContext, useUserContext } from '../../contexts/UserContext';
import {
    shouldAllowHeader,
    shouldAllowNotifications,
} from '../../utils/conditionals';
import { useModals } from '../hooks/modalHooks';
import SocialLoginOptions from '../socialLoginOptions/SocialLoginOptions';

const getRecipientData = (isUsingOrderId?: boolean, referrer?: string) => {
    if (!referrer || isUsingOrderId) {
        return { recipient: null, recipient_type: null };
    }
    const decodedReferrer = decodeURIComponent(referrer);
    const orderIdMatch = decodedReferrer.match(/\/mytickets(?:-web)?\/(\d+)/);
    const orderId = orderIdMatch ? orderIdMatch[1] : null;

    const parsedRecipientUrl = new URL(global.window.location.href);
    const queryString = parsedRecipientUrl.search;
    const urlParams = new URLSearchParams(queryString);
    function getQueryParam(param: string): string | null {
        return urlParams.get(param);
    }
    const recipient = getQueryParam('recipient');
    const recipient_type = Number(getQueryParam('recipient_type'));

    if (orderId && recipient) {
        return { recipient, recipient_type };
    }

    return { recipient: null, recipient_type: null };
};

const Lookup = ({ routing }: { routing?: any }) => {
    const configContext: IConfigContextProps = useConfigContext();
    const frontendContext: IFrontendContext = useFrontendContext();
    const userContext: IUserContext = useUserContext();
    const modal = useModals();

    let contentHeader = null;
    if (shouldAllowHeader(configContext)) {
        const Header = loadable(() => import('../../components/common/Header'));
        contentHeader = (
            <Header
                titleText={
                    configContext.header?.titleText ??
                    gettext('Create an account')
                }
                subtitleText={configContext.header?.subtitleText}
                headerLinkProps={configContext.header?.headerLinkProps}
            />
        );
    }
    const { recipient, recipient_type } = getRecipientData(
        configContext.isUsingOrderId,
        configContext.referrer,
    );
    if (recipient) {
        const modalType = recipient_type
            ? MODAL_TYPES.passwordless
            : MODAL_TYPES.softAccountActivation;

        frontendContext?.methods.setShowModal(modalType);
        userContext?.methods.lookup.setLookedAccount(recipient);
        configContext.isUsingOrderId = true;
    }
    let contentNotification = null;
    if (shouldAllowNotifications(configContext)) {
        const Notifications = loadable(
            () => import('../../components/common/Notifications'),
        );

        contentNotification = <Notifications section="signup" />;
    }

    return (
        <>
            {contentHeader}

            {contentNotification}

            <LookupForm
                onSubmit={({
                    values,
                    contexts,
                }: {
                    values: { email: string };
                    contexts: IAllContextsProps;
                }) => {
                    lookUpUserAction({
                        data: values,
                        isSignup: true,
                        contexts,
                    });
                }}
                buttonCTA={
                    configContext.signupMethods?.submitButtonText?.lookup ??
                    gettext('Continue')
                }
                shouldAnimate={false}
            />

            {modal}

            {!configContext.hideDivider && <FormDivider />}

            <div className="eds-l-pad-bot-5" data-testid="social-login-section">
                <SocialLoginOptions routing={routing} isSignupFlow={true} />
            </div>
        </>
    );
};

export default Lookup;
